import { RepliersListing } from '../../types/repliers/RepliersListing';
import { DateTime } from 'luxon';
import { DATE_YYYY_MM_DD } from '../../constants/dateTimes';
import { TORONTO_BOARD_RESOURCE_CODE } from '../../constants/realEstateBoards';

const SINGLE_NUMBER_REGEX = /^\d+$/;
const TWO_NUMBERS_RANGE_REGEX = /^(\d+)-(\d+)$/;
const SINGLE_NUMBER_PLUS_REGEX = /^(\d+)\+$/;

const OPEN_ENDED_RANGE_YEARS = 100;

const NUM_OF_YEARS_IN_THE_FUTURE_BOUND = 5;
const LOWER_BOUND_FOR_YEAR_BUILT = 1700;

export default function getDateBuiltRangeFromRepliersListing(
  repliersListing: RepliersListing,
): string | null {
  const yearBuilt = repliersListing.details.yearBuilt;

  if (!yearBuilt) return null;

  const listDateTime = DateTime.fromISO(repliersListing.listDate).toUTC();
  let yearsAgoBuiltMin;
  let yearsAgoBuiltMax;

  if (repliersListing.resource === TORONTO_BOARD_RESOURCE_CODE) {
    if (yearBuilt === 'New' || yearBuilt === 'New.') {
      // Handle "New" or "New."
      yearsAgoBuiltMin = 0;
      yearsAgoBuiltMax = 1;
    } else if (yearBuilt.match(SINGLE_NUMBER_REGEX)) {
      // Handle cases like 0, 1, 2, etc.
      const yearsAgoBuilt = Number(yearBuilt);

      yearsAgoBuiltMin = yearsAgoBuilt;
      yearsAgoBuiltMax = yearsAgoBuilt + 1;
    } else if (yearBuilt.match(TWO_NUMBERS_RANGE_REGEX)) {
      // Handle cases like 0-5, 6-10, etc.
      const twoNumbersRangeMatch = yearBuilt.match(TWO_NUMBERS_RANGE_REGEX);
      let firstNumber = Number(twoNumbersRangeMatch[1]);
      if (firstNumber === 0) {
        // If there is a range like 0-5, we assume it's not built in the last year and adjust the range to 1-5.
        firstNumber = 1;
      }
      const secondNumber = Number(twoNumbersRangeMatch[2]);

      // Return null if the range is invalid, like 61-50
      if (firstNumber > secondNumber) return null;

      yearsAgoBuiltMin = firstNumber;
      yearsAgoBuiltMax = secondNumber + 1;
    } else if (yearBuilt.match(SINGLE_NUMBER_PLUS_REGEX)) {
      // Handle cases like 100+
      const singleNumberPlusMatch = yearBuilt.match(SINGLE_NUMBER_PLUS_REGEX);
      const number = Number(singleNumberPlusMatch[1]);

      yearsAgoBuiltMin = number;
      yearsAgoBuiltMax = number + OPEN_ENDED_RANGE_YEARS + 1;
    } else {
      return null;
    }

    const rangeEnd = listDateTime
      .minus({ years: yearsAgoBuiltMin })
      .toFormat(DATE_YYYY_MM_DD);
    const rangeStart = listDateTime
      .minus({ years: yearsAgoBuiltMax })
      .toFormat(DATE_YYYY_MM_DD);

    return `[${rangeStart},${rangeEnd})`;
  } else {
    // ITSO examples: 2023, 2022, 2021, etc.
    if (!yearBuilt.match(SINGLE_NUMBER_REGEX)) {
      return null;
    }
    const yearBuiltNumber = Number(yearBuilt);

    const listDateYearNumber = Number(listDateTime.toFormat('yyyy'));
    if (
      yearBuiltNumber > listDateYearNumber + NUM_OF_YEARS_IN_THE_FUTURE_BOUND ||
      yearBuiltNumber < LOWER_BOUND_FOR_YEAR_BUILT
    ) {
      return null;
    }

    const rangeStart = DateTime.fromObject({ year: yearBuiltNumber }).toFormat(
      DATE_YYYY_MM_DD,
    );
    const rangeEnd = DateTime.fromObject({ year: yearBuiltNumber })
      .plus({ years: 1 })
      .toFormat(DATE_YYYY_MM_DD);

    return `[${rangeStart},${rangeEnd})`;
  }
}

/*
// REFERENCE

TREB yearBuilt values:

NULL
0-5
6-15
New
16-30
31-50
51-99
6-10
100+
11-15
1
0
New.
2
3
4
5
6
7

ITSO/CREA yearBuilt examples:

NULL
2024
2023
2022
2021
... (etc)
0
9999
1
79
13

 */
